import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  ASSESSMENT_LABELS,
  AssessmentType,
} from '../../actions/matters/createRafAssessment';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];
const ACCEPTED_EXT = '.pdf,.docx,.jpg,.jpeg,.png';
const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 30;

const ASSESSMENT_HELPER: Record<AssessmentType, string> = {
  merits:
    'Upload the police report / Officer\'s Accident Report, witness statements, claimant affidavits, and any photographs or sketch plans you want considered.',
  quantum:
    'Upload medical records, RAF 4 / specialist reports, hospital accounts, payslips / employer letters, and any actuarial or industrial-psychology reports.',
};

interface AssessmentUploadModalProps {
  open: boolean;
  assessmentType: AssessmentType;
  onClose: () => void;
  onSubmit: (
    files: File[],
    options: { representedParty?: string; additionalContext?: string },
  ) => void;
}

const AssessmentUploadModal: React.FC<AssessmentUploadModalProps> = ({
  open,
  assessmentType,
  onClose,
  onSubmit,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [representedParty, setRepresentedParty] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setFileError('');
      setRepresentedParty('');
      setAdditionalContext('');
    }
  }, [open, assessmentType]);

  const addFiles = (newFiles: File[]) => {
    setFileError('');
    const oversized = newFiles.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      setFileError(`${oversized.name} exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
      return;
    }
    setFiles((prev) => {
      if (prev.length + newFiles.length > MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed.`);
      }
      return [...prev, ...newFiles].slice(0, MAX_FILES);
    });
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      ACCEPTED_TYPES.includes(f.type),
    );
    addFiles(dropped);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const handleSubmit = () => {
    if (files.length === 0) {
      setFileError('Upload at least one supporting document.');
      return;
    }
    onSubmit(files, {
      representedParty: representedParty.trim() || undefined,
      additionalContext: additionalContext.trim() || undefined,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 560 },
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'var(--bg)',
          color: 'var(--t1)',
          borderRadius: 'var(--radius)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Typography
              data-testid="assessment-modal-title"
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontSize: '1.15rem',
                fontWeight: 700,
                color: 'var(--t1)',
              }}
            >
              {ASSESSMENT_LABELS[assessmentType]}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'var(--t3)', mt: 0.25 }}>
              Upload supporting documents and run the assessment.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close assessment upload"
            data-testid="assessment-modal-close"
            sx={{ color: 'var(--t3)' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: '0.8rem', color: 'var(--t3)', mb: 1.5 }}>
          {ASSESSMENT_HELPER[assessmentType]}
        </Typography>

        {/* Dropzone — visually identical to the main MatterDetailView dropzone */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="assessment-dropzone"
          sx={{
            border: '1.5px dashed rgba(26,33,102,0.25)',
            borderRadius: 'var(--radius)',
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:hover': { borderColor: 'var(--teal)', bgcolor: 'var(--teal-dim)' },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 36, color: 'var(--teal)', mb: 1 }} />
          <Typography sx={{ color: 'var(--t2)', fontSize: '0.85rem', fontWeight: 500 }}>
            Drag & drop files here, or click to browse
          </Typography>
          <Typography sx={{ color: 'var(--t4)', fontSize: '0.74rem', mt: 0.5 }}>
            PDF, DOCX, JPG, PNG — up to {MAX_FILE_SIZE_MB} MB each, max {MAX_FILES} files
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={ACCEPTED_EXT}
            onChange={(e) => {
              if (e.target.files) addFiles(Array.from(e.target.files));
              e.target.value = '';
            }}
          />
        </Box>

        {files.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {files.map((f, i) => (
              <Box
                key={`${f.name}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'var(--s1)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}
              >
                <InsertDriveFileIcon sx={{ fontSize: 18, color: 'var(--teal)' }} />
                <Typography sx={{ flex: 1, fontSize: '0.8rem', color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--t4)', flexShrink: 0 }}>
                  {(f.size / 1024).toFixed(0)} KB
                </Typography>
                <IconButton size="small" onClick={() => removeFile(i)} sx={{ color: 'var(--t4)', '&:hover': { color: 'var(--err)' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {fileError && (
          <Typography sx={{ color: 'var(--err)', fontSize: '0.8rem', mt: 1 }}>
            {fileError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          <TextField
            label="Who do you represent?"
            placeholder="e.g. The plaintiff, Mr Sipho Dlamini"
            value={representedParty}
            onChange={(e) => setRepresentedParty(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'assessment-represented-party' }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              '& .MuiInputLabel-root': { fontSize: '0.85rem', color: 'var(--t3)' },
            }}
          />
          <TextField
            label="Additional context (optional)"
            placeholder="Anything the AI should know — e.g. hit-and-run, paraplegia, prior settlement offers."
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            inputProps={{ 'data-testid': 'assessment-additional-context' }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              '& .MuiInputLabel-root': { fontSize: '0.85rem', color: 'var(--t3)' },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: 'var(--t3)',
              fontSize: '0.82rem',
              '&:hover': { bgcolor: 'transparent', color: 'var(--t1)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            data-testid="assessment-submit-btn"
            disabled={files.length === 0}
            onClick={handleSubmit}
            sx={{
              bgcolor: 'var(--teal)',
              color: 'var(--bg)',
              fontWeight: 600,
              fontSize: '0.84rem',
              textTransform: 'none',
              borderRadius: 'var(--radius-sm)',
              px: 2.5,
              '&:hover': { bgcolor: 'var(--teal-mid)' },
              '&.Mui-disabled': { bgcolor: 'var(--s3)', color: 'var(--t4)' },
            }}
          >
            Run Assessment
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssessmentUploadModal;
